import React from "react";  
import { 
  FaIndustry, 
  FaTools, 
  FaCogs, 
  FaWarehouse, 
  FaTruck, 
  FaRobot,
  FaArrowRight,
  FaCheck,
} from "react-icons/fa";
import { MdPrecisionManufacturing } from "react-icons/md";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChallengesImage from "../assets/manufacturing_challenges.jpeg";
import heroBg from "../assets/manufacturing.webp";   // adjust path as needed


const ManufacturingIndustry = () => {
  const services = [
  {
    icon: FaIndustry,
    title: "Industrial Production",
    description:
      "High-quality large scale manufacturing solutions for diverse industries",
    features: [
      "Mass Production",
      "Quality Control",
      "Lean Manufacturing",
      "Process Optimization",
    ],
    color: "from-blue-500 to-cyan-500",
    iconColor: "text-white",
  },
  {
    icon: FaRobot,
    title: "Automation Solutions",
    description:
      "Advanced automation systems to increase productivity and efficiency",
    features: [
      "Robotic Automation",
      "PLC Systems",
      "Smart Machinery",
      "Predictive Maintenance",
    ],
    color: "from-purple-500 to-fuchsia-500",
    iconColor: "text-white",
  },
  {
    icon: FaTools,
    title: "Custom Fabrication",
    description:
      "Precision fabrication services designed for specific industry needs",
    features: [
      "Metal Fabrication",
      "Prototype Development",
      "Machining Services",
      "Assembly Solutions",
    ],
    color: "from-orange-500 to-amber-500",
    iconColor: "text-white",
  },
  {
    icon: MdPrecisionManufacturing,
    title: "Process Engineering",
    description:
      "Optimized manufacturing workflows for better output and reduced waste",
    features: [
      "Process Design",
      "Cost Optimization",
      "Workflow Automation",
      "Efficiency Audits",
    ],
    color: "from-green-500 to-emerald-500",
    iconColor: "text-white",
  },
  {
    icon: FaWarehouse,
    title: "Supply Chain Management",
    description:
      "End-to-end material handling and inventory control solutions",
    features: [
      "Inventory Tracking",
      "Vendor Management",
      "Warehouse Optimization",
      "Logistics Support",
    ],
    color: "from-red-500 to-rose-500",
    iconColor: "text-white",
  },
  {
    icon: FaTruck,
    title: "Logistics & Distribution",
    description:
      "Reliable product transportation and nationwide distribution services",
    features: [
      "Fleet Management",
      "Real-time Tracking",
      "On-time Delivery",
      "Distribution Planning",
    ],
    color: "from-teal-500 to-cyan-500",
    iconColor: "text-white",
  },
];

  const segments = [
  {
    category: "Manufacturing Services",
    items: [
      "Industrial Production",
      "Custom Fabrication",
      "Automation Systems",
      "Process Engineering",
      "Quality Control",
      "Lean Manufacturing",
      "Machine Installation",
      "Maintenance Services",
    ],
    icon: FaIndustry,
    iconColor: "text-white",
  },
  {
    category: "Industry Types",
    items: [
      "Automobile Manufacturing",
      "Electronics Manufacturing",
      "Textile Industry",
      "Pharmaceutical Plants",
      "Food Processing Units",
      "Heavy Machinery Plants",
      "Metal Industries",
      "Chemical Manufacturing",
    ],
    icon: FaCogs,
    iconColor: "text-white",
  },
  {
    category: "Operational Support",
    items: [
      "Supply Chain Management",
      "Warehouse Operations",
      "Material Handling",
      "Production Planning",
      "Inventory Management",
      "Logistics Support",
      "Safety Compliance",
      "Technical Consulting",
    ],
    icon: FaTools,
    iconColor: "text-white",
  },
];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="min-h-screen">

{/* HERO SECTION */}
<div
  className="relative bg-cover bg-center bg-no-repeat text-white py-24 overflow-hidden"
  style={{ backgroundImage: `url(${heroBg})` }}
>
  <div className="absolute inset-0 bg-black/60"></div>

  <div className="container mx-auto px-6 relative z-10 text-center">

    <h1 className="text-6xl font-bold text-white mb-6 pb-3">
      Manufacturing Industry
    </h1>

    <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
      Powering industries with advanced manufacturing solutions and smart automation.
    </p>
  </div>
</div>

{/* SERVICES SECTION */}
<div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative">
  <div className="container mx-auto px-6 relative">
    <div className="text-center mb-16">
      <h2 className="text-5xl font-bold text-white mb-6">
        Manufacturing Services
      </h2>
      <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
        End-to-end manufacturing solutions for modern industries
      </p>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {services.map((service, index) => (
        <div
          key={index}
          className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-700 hover:border-blue-500/50 hover:-translate-y-2"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-2xl shadow-lg flex items-center justify-center">
              <service.icon className={`text-2xl ${service.iconColor}`} />
            </div>
            <h3 className="text-2xl font-bold">{service.title}</h3>
          </div>

          <p className="text-gray-300 leading-relaxed text-lg mb-6">
            {service.description}
          </p>

          <div className="space-y-3">
            {service.features.map((feature, i) => (
              <div key={i} className="flex items-center">
                <div className="bg-cyan-300/20 p-1 rounded-lg mr-3">
                  <FaCheck className="text-blue-400 text-sm" />
                </div>
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
</div>

{/* CHALLENGES SECTION */}
<div className="pt-10 pb-20 bg-white">
  <div className="container mx-auto px-6">
    <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
      Challenges Faced by the Manufacturing Industry
    </h2>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

        <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6">
          <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">01</div>
          <h3 className="font-bold text-gray-900 mb-2">High Production Costs</h3>
          <p className="text-gray-600">Streamlined processes and automation reduce operational expenses.</p>
        </div>

        <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6">
          <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">02</div>
          <h3 className="font-bold text-gray-900 mb-2">Equipment Downtime</h3>
          <p className="text-gray-600">Predictive maintenance prevents unexpected breakdowns.</p>
        </div>

        <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6">
          <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">03</div>
          <h3 className="font-bold text-gray-900 mb-2">Supply Chain Disruptions</h3>
          <p className="text-gray-600">Optimized logistics keep production running smoothly.</p>
        </div>

        <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6">
          <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">04</div>
          <h3 className="font-bold text-gray-900 mb-2">Labor Skill Gaps</h3>
          <p className="text-gray-600">Smart automation bridges workforce limitations.</p>
        </div>

      </div>

      <div className="flex justify-center">
        <img src={ChallengesImage} alt="Challenges" className="rounded-3xl shadow-2xl w-full max-w-xl object-cover" />
      </div>

    </div>
  </div>
</div>
{/* 🌟 NEW SECTION — How Amplinova turns Manufacturing challenges into opportunities */}
<div className="pt-10 pb-20 bg-white">
  <div className="container mx-auto px-6">

    {/* Heading */}
    <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
      How <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent underline decoration-blue-400 decoration-4">Amplinova</span> turns Manufacturing challenges into opportunity
    </h2>

    {/* 4 Items — Side by side */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">

      {/* 01 */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
          <FaCheck className="text-white text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Smart Automation
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Convert manual workflows into automated production systems to boost speed, accuracy, and efficiency.
        </p>
      </div>

      {/* 02 */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
          <FaCheck className="text-white text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Cost Optimization
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Reduce raw material waste, energy consumption, and production downtime using data-driven strategies.
        </p>
      </div>

      {/* 03 */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
          <FaCheck className="text-white text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Predictive Maintenance
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Prevent machine failures with AI-powered monitoring and predictive maintenance planning.
        </p>
      </div>

      {/* 04 */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
          <FaCheck className="text-white text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Scalable Growth
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Expand production capacity smoothly with cloud systems, ERP solutions, and smart factory planning.
        </p>
      </div>

    </div>
  </div>
</div>


{/* SEGMENTS SECTION */}
<div className="pt-10 pb-20 bg-white relative">
  <div className="container mx-auto px-6 relative">
    <div className="text-center mb-16">
      <h2 className="text-5xl font-bold text-gray-900 mb-6">
        Manufacturing Segments We Serve
      </h2>
      <p className="text-xl text-gray-600 max-w-2xl mx-auto">
        Industrial solutions across multiple manufacturing sectors
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-8">
      {segments.map((segment, index) => (
        <div
          key={index}
          className="bg-white rounded-2xl p-8 shadow-lg border border-blue-100"
        >
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 rounded-2xl inline-flex mb-6 shadow-lg">
            <segment.icon className={`text-2xl ${segment.iconColor}`} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            {segment.category}
          </h3>
          <div className="space-y-3">
            {segment.items.map((item, itemIndex) => (
              <div key={itemIndex} className="flex items-center text-gray-700">
                <div className="bg-green-100 p-1 rounded-lg mr-3">
                  <FaCheck className="text-green-600 text-sm" />
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
</div>

{/* CTA SECTION */}
<div className="relative pt-10 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
  <div className="absolute inset-0 bg-black opacity-40"></div>

  <div className="container mx-auto px-6 text-center relative z-10">
    <h2 className="text-5xl font-bold text-white mb-8">
      Ready to Transform Your Manufacturing Business?
    </h2>
    <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
      Increase productivity, reduce costs, and scale your manufacturing operations with smart automation.
    </p>

    <button
      onClick={() => (window.location.href = "/contact-us")}
      className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-5 rounded-xl font-semibold hover:scale-105 transition flex items-center gap-3 mx-auto">
      Get Started Now
      <FaArrowRight />
    </button>
  </div>
</div>

</div>
<Footer />
</div>
);
};

export default ManufacturingIndustry;
